import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { IconButtonWithText } from '../../../Common';
import ActivityDrawer from './ActivityDrawer';
import { useHttpGetActivityFeedWithUser, useHttpGetActivityFeedByClientId } from '../../../../hooks/acs/acsApi';
import { ActivityType, MeetActivityWithUserDto, NoteActivityWithUserDto } from '../../../../commonTypes/project.types';
import { formatDateToDayMonthYear, formatDateAndTime } from '../../../../util/date/dateFormater';
import { invalidateActivityFeedCache, invalidateActivityFeedByClientCache } from '../../../../store/slices/projects.slice';

interface Activity {
    id: string;
    type: ActivityType;
    title: string;
    note: string;
    createdAt: Date;
    createdBy: string;
    createdByName: string;
    createdByImageUrl?: string;
    projectName?: string;
    date?: string;
    time?: string;
    link?: string;
}

interface ActivityFeedProps {
    projectId: string;
    clientId: string;
}

export const ActivityFeed = ({ projectId, clientId }: ActivityFeedProps) => {
    const dispatch = useDispatch();
    const [isActivityDrawerOpen, setIsActivityDrawerOpen] = useState(false);

    const isClientPage = !projectId;
    const { activityFeed: projectActivityFeed, exec: execProject } = useHttpGetActivityFeedWithUser(projectId);
    const { activityFeed: clientActivityFeed, exec: execClient } = useHttpGetActivityFeedByClientId(isClientPage ? clientId : undefined);

    const activityFeed = isClientPage ? clientActivityFeed : projectActivityFeed;
    const exec = isClientPage ? execClient : execProject;

    const activities = useMemo(() => {
        if (!activityFeed) return [];

        const noteActivities: Activity[] = activityFeed.noteActivities.map((note: NoteActivityWithUserDto) => ({
            id: note.id,
            type: ActivityType.NOTE,
            title: note.title,
            note: note.description,
            createdAt: new Date(note.createdAt),
            createdBy: note.createdBy,
            createdByName: note.createdByName,
            createdByImageUrl: note.createdByImageUrl,
            projectName: note.projectName,
        }));

        const meetActivities: Activity[] = activityFeed.meetActivities.map((meet: MeetActivityWithUserDto) => ({
            id: meet.id,
            type: ActivityType.MEET,
            title: meet.title,
            note: meet.description,
            createdAt: new Date(meet.createdAt),
            createdBy: meet.createdBy,
            createdByName: meet.createdByName,
            createdByImageUrl: meet.createdByImageUrl,
            projectName: meet.projectName,
            date: meet.date,
            time: meet.time,
            link: meet.link,
        }));

        return [...noteActivities, ...meetActivities].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    }, [activityFeed]);

    const handleActivitySuccess = () => {
        if (isClientPage) {
            dispatch(invalidateActivityFeedByClientCache(clientId));
        } else {
            dispatch(invalidateActivityFeedCache(projectId));
        }
        setIsActivityDrawerOpen(false);
        exec(true);
    };

    const formatTime = (timeString: string) => {
        const parts = timeString.split(':');
        if (parts.length >= 2) {
            const hours = parts[0].padStart(2, '0');
            const minutes = parts[1].padStart(2, '0');
            return `${hours}:${minutes}`;
        }
        return timeString;
    };

    return (
        <div className="activity-feed">
            <div className="activity-feed__header">
                <h3>Activity Feed</h3>
                <IconButtonWithText
                    text="Add New Activity"
                    variant="text"
                    iconSrc="/svg/plus-blue-without-circle.svg"
                    onClick={() => setIsActivityDrawerOpen(true)}
                />
            </div>

            <div className="activity-feed__list">
                {activities.length === 0 ? (
                    <div className="activity-feed__empty">
                        <p className="activity-feed__empty-text">No activities yet</p>
                        <p className="activity-feed__empty-subtext">Add your first activity to get started</p>
                    </div>
                ) : (
                    activities.map(activity => (
                        <div key={activity.id} className="activity-item">
                            <div className="activity-item__header">
                                <div className="activity-item__header-left">
                                    <div className="activity-item__info">
                                        <h4 className="activity-item__title">{activity.title}</h4>
                                        {isClientPage && activity.projectName && (
                                            <p className="activity-item__project-name">{activity.projectName}</p>
                                        )}
                                    </div>
                                </div>
                                <span className={`activity-item__tag activity-item__tag--${activity.type}`}>
                                    {activity.type === 'note' ? 'Note' : 'Meet'}
                                </span>
                            </div>
                            <p className="activity-item__note">{activity.note}</p>

                            {activity.type === 'meet' && (activity.date || activity.time || activity.link) && (
                                <div className="activity-item__meet-details">
                                    {activity.date && (
                                        <div className="activity-item__detail-row">
                                            <span className="activity-item__detail-label">Date</span>
                                            <span className="activity-item__detail-separator">/</span>
                                            <span className="activity-item__detail-value">{formatDateToDayMonthYear(activity.date)}</span>
                                        </div>
                                    )}
                                    {activity.time && (
                                        <div className="activity-item__detail-row">
                                            <span className="activity-item__detail-label">Time</span>
                                            <span className="activity-item__detail-separator">/</span>
                                            <span className="activity-item__detail-value">{formatTime(activity.time)}</span>
                                        </div>
                                    )}
                                    {activity.link && (
                                        <div className="activity-item__detail-row">
                                            <span className="activity-item__detail-label">Meet Link</span>
                                            <span className="activity-item__detail-separator">/</span>
                                            <a href={activity.link} target="_blank" rel="noopener noreferrer" className="activity-item__detail-link">
                                                Link
                                            </a>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="activity-item__creator">
                                <span className="activity-item__creator-label">Created by</span>
                                <div className="activity-item__creator-info">
                                    <div className="activity-item__creator-avatar">
                                        <img
                                            src={activity.createdByImageUrl || '/svg/default-avatar.svg'}
                                            alt="User avatar"
                                            className="activity-item__creator-avatar-img"
                                        />
                                    </div>
                                    <div className="activity-item__creator-details">
                                        <span className="activity-item__creator-name">{activity.createdByName}</span>
                                        <span className="activity-item__creator-time">{formatDateAndTime(activity.createdAt)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <ActivityDrawer
                open={isActivityDrawerOpen}
                onClose={() => setIsActivityDrawerOpen(false)}
                projectId={projectId}
                clientId={clientId}
                onSuccess={handleActivitySuccess}
            />
        </div>
    );
};
